import { HTTP_INTERCEPTORS, HttpBackend, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { inject, NgModule, provideAppInitializer } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { provideServiceWorker } from '@angular/service-worker';
import { MenubarComponent } from "@navigation/components/menubar/menubar.component";
import { filter, map, take } from 'rxjs/operators';

// third-party modules
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsFormPluginModule } from '@ngxs/form-plugin';
import { NgxsRouterPluginModule } from '@ngxs/router-plugin';
import { NgxsStoragePluginModule, StorageOption } from '@ngxs/storage-plugin';
import { NgxsModule } from '@ngxs/store';
import moment from 'moment';
import { ModalModule } from 'ngx-bootstrap/modal';
import { MultiTranslateHttpLoader } from 'ngx-translate-multi-http-loader';

// bayclubs modules
import { AuditLogViewerModule } from '@bayclubs/audit-log-viewer';
import { BcCalendarModule } from '@bayclubs/calendar';
import { BASE_PATH as CB_OFFICE_BASE_PATH, CourtBookingEmployeeApiApiModule } from '@bayclubs/court-booking-employee-api';
import { DropDownModule } from '@bayclubs/drop-down';
import {
    BC_PRIVILEGES_PROVIDERS,
    BcEmployeeAuthEnvironment,
    BcEmployeeAuthFacade,
    BcEmployeeAuthInterceptor,
    BcEmployeeAuthModule,
    BcEmployeeAuthState
} from '@bayclubs/employee-auth';
import {
    ApplicationModelV2, ApplicationsService,
    BASE_PATH as EMPLOYEE_API_BASE_PATH
} from '@bayclubs/employee-management-api';
import {
    AppVersionFooter,
    AuthService as HorizonAuthService,
    Toast,
    isDeployedRuntimeEnvironment,
    provideApplicationGroupsLoader,
    provideAuth,
    provideClubsLoader,
    provideHorizonErrorHandling,
    provideHorizonLogging,
    provideHorizonLoginFactory,
    provideHorizonUserFactory,
    provideMenubarLogoHref,
    providePrimeng,
    provideVersionCheck,
    provideHorizonErrorHandlingTesting
} from '@bayclubs/horizon';
import type { HorizonLoggingConfig } from '@bayclubs/horizon';
import { BcRecurringModule } from '@bayclubs/recurring';
import { SpinnerModule } from '@bayclubs/spinner';

// app modules
import { AuthModule } from '@auth/auth.module';
import { DualAuthInterceptor } from '@auth/services/dual-auth.interceptor';
import { BlockedSlotSaveModule } from '@blocked-slot-save/blocked-slot-save.module';
import {
    CoreFacade,
    CoreModule,
    CoreState,
    OfficePrivilegesProviderService
} from '@core/core.module';
import { CourtBookingSaveModule } from '@court-booking-save/court-booking-save.module';
import { CourtEventInfoModule } from '@court-event-info/court-event-info.module';
import { CourtEventModalsModule } from '@court-event-modals/court-event-modals.module';
import { CourtEventNoteSaveModule } from '@court-event-note-save/court-event-note-save.module';
import { environment } from '@environments/environment';
import { NavigationModule } from '@navigation/navigation.module';
import { PlayersModule } from '@players/players.module';

import { BayclubsUiCoreModule, TimeDisplayPipe, TranslationService } from '@bayclubs/ui-core';
import { BayclubsUiDatePickerModule } from '@bayclubs/ui-date-picker';
import { provideNgxMask } from 'ngx-mask';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { CourtEventDetailsModule } from './court-event-details/court-event-details.module';

const horizonLoggingConfig: Partial<HorizonLoggingConfig> = {
    appVersion: environment.appVersion,
    seq: {
        enabled: !!environment.seqLoggingEndpoint,
        endpoint: environment.seqLoggingEndpoint,
        apiKey: environment.seqLoggingApiKey
    }
};

const legacyLoggingConfig = {
    seqLoggingApiKey: environment.seqLoggingApiKey,
    seqLoggingEndpoint: environment.seqLoggingEndpoint,
    consoleLogEnabled: false,
    appVersion: environment.appVersion
};

@NgModule({ declarations: [AppComponent],
    bootstrap: [AppComponent],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        FormsModule,
        ReactiveFormsModule,
        NgxsModule.forRoot([], { developmentMode: !environment.production }),
        NgxsRouterPluginModule.forRoot(),
        NgxsStoragePluginModule.forRoot({
            keys: [BcEmployeeAuthState, CoreState],
            storage: StorageOption.LocalStorage
        }),
        NgxsReduxDevtoolsPluginModule.forRoot({ disabled: environment.production }),
        NgxsFormPluginModule.forRoot(),
        NavigationModule,
        AppRoutingModule,
        BcEmployeeAuthModule.forRoot({
            accessDeniedRoute: '/forbidden',
            loginRoute: '/login',
            adClientId: environment.adClientId,
            adTenant: environment.adTenant,
            authenticationBaseUrl: environment.authenticationBaseUrl,
            baseHref: environment.baseHref
        } as BcEmployeeAuthEnvironment),
        AuthModule,
        AuditLogViewerModule.forRoot({
            endpoint: environment.auditLogUrl
        }),
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: createTranslateLoader,
                deps: [HttpBackend]
            }
        }),
        CoreModule,
        DropDownModule.forRoot(),
        SpinnerModule.forRoot(),
        ModalModule.forRoot(),
        BcCalendarModule,
        CourtBookingEmployeeApiApiModule,
        BcRecurringModule,
        BlockedSlotSaveModule,
        CourtEventInfoModule,
        PlayersModule,
        CourtBookingSaveModule,
        CourtEventDetailsModule,
        CourtEventModalsModule,
        CourtEventNoteSaveModule,
        BayclubsUiCoreModule.forRoot(),
        BayclubsUiDatePickerModule,
        AppVersionFooter,
        MenubarComponent,
        Toast
    ],
    providers: [
        { provide: 'AuthEnvironment', useValue: environment },
        { provide: 'loggingConfig', useValue: legacyLoggingConfig },
        { provide: CB_OFFICE_BASE_PATH, useValue: environment.officeApiUrl },
        {
            provide: BC_PRIVILEGES_PROVIDERS,
            useClass: OfficePrivilegesProviderService,
            multi: true
        },
        provideAppInitializer(() => {
            const initializerFn = initTranslations(inject(TranslationService));
            return initializerFn();
        }),
        {
            provide: HTTP_INTERCEPTORS,
            useClass: DualAuthInterceptor,
            multi: true
        },
        { provide: EMPLOYEE_API_BASE_PATH, useValue: environment.employeeApiUrl },
        TimeDisplayPipe,
        provideNgxMask(),
        provideHttpClient(withInterceptorsFromDi()),
        provideServiceWorker('ngsw-worker.js', {
            enabled: isDeployedRuntimeEnvironment()
        }),
        providePrimeng(),
        provideVersionCheck(),
        provideMenubarLogoHref(environment.officeAppUrl),
        provideApplicationGroupsLoader<ApplicationModelV2>({
            loaderFactory: () => {
                const api = inject(ApplicationsService);
                return () => api.api10ApplicationsV2Get();
            },
            autoInit: false
        }),
        provideClubsLoader({
            loaderFactory: () => {
                const coreFacade = inject(CoreFacade);
                return () => {
                    const snapshot = coreFacade.clubsSnapshot?.();
                    if (snapshot?.loaded || snapshot?.loadingError) {
                        return { clubs: snapshot?.items ?? [] };
                    }

                    coreFacade.loadContext();
                    return coreFacade.clubs$.pipe(
                        filter(context => context?.loaded || context?.loadingError),
                        map(context => ({ clubs: context?.items ?? [] })),
                        take(1)
                    );
                };
            },
            autoInit: false
        }),
        provideHorizonLogging({
            config: horizonLoggingConfig
        }),
        ...(environment.isMsalAuthenticationEnabled
            ? []
            : [provideHorizonUserFactory((auth) => {
                const token = (auth as BcEmployeeAuthFacade).getTokenSnapshot();

                return token ? {
                    id: token.id,
                    name: token.name,
                    login: token.login,
                    email: token.upn
                } : null;
            }, [BcEmployeeAuthFacade])]),
        provideHorizonErrorHandling(),
        provideHorizonErrorHandlingTesting(),
        ...(environment.isMsalAuthenticationEnabled
            ? [provideAuth({
                config: {
                    clientId: environment.adClientId,
                    tenantId: environment.adTenant,
                    redirectUri: environment.adRedirectUri,
                    protectedResources: {
                        [environment.officeApiUrl + '/*']: environment.officeApiScope,
                        [environment.employeeApiUrl + '/*']: environment.employeeApiScope,
                    },
                    loginFailedRoute: '/login',
                    returnUrlStrategy: 'remember',
                },
            })]
            : [BcEmployeeAuthInterceptor]),
        ...(environment.isMsalAuthenticationEnabled
            ? [provideHorizonLoginFactory((authService: HorizonAuthService) => ({
                isAuthenticated: () => authService.isAuthenticated(),
                login: () => authService.loginRedirect()
            }), [HorizonAuthService])]
            : [provideHorizonLoginFactory((authFacade: BcEmployeeAuthFacade) => ({
                isAuthenticated: () => authFacade.getIsAuthenticatedSnapshot(),
                login: () => authFacade.login()
            }), [BcEmployeeAuthFacade])])
    ] })
export class AppModule {
    constructor() {
        console.log(environment.appVersion);
        moment.locale('en');
    }
}

// AoT requires an exported function for factories
export function createTranslateLoader(http: HttpBackend) {
    return new MultiTranslateHttpLoader(http, [
        { prefix: 'l10n/common/', suffix: '.json?timestamp=' + new Date().getTime() },
        { prefix: 'l10n/auth/', suffix: '.json?timestamp=' + new Date().getTime() },
        { prefix: 'l10n/navigation/', suffix: '.json?timestamp=' + new Date().getTime() },
        { prefix: 'l10n/court-events/', suffix: '.json?timestamp=' + new Date().getTime() },
        { prefix: 'l10n/court-management/', suffix: '.json?timestamp=' + new Date().getTime() },
        { prefix: 'l10n/recurring/', suffix: '.json?timestamp=' + new Date().getTime() },
        { prefix: 'l10n/blocked-slot-save/', suffix: '.json?timestamp=' + new Date().getTime() },
        { prefix: 'l10n/event-info/', suffix: '.json?timestamp=' + new Date().getTime() },
        { prefix: 'l10n/court-sheet/', suffix: '.json?timestamp=' + new Date().getTime() },
        { prefix: 'l10n/players/', suffix: '.json?timestamp=' + new Date().getTime() },
        { prefix: 'l10n/court-booking-save/', suffix: '.json?timestamp=' + new Date().getTime() },
        { prefix: 'l10n/rules-matrix/', suffix: '.json?timestamp=' + new Date().getTime() },
        { prefix: 'l10n/court-event-note-save/', suffix: '.json?timestamp=' + new Date().getTime() }
    ]);
}

export function initTranslations(translationService: TranslationService) {
    return translationService.initSynchronously('locale-en');
}
